import { useState, useEffect } from 'react';
import { Users, Monitor, Loader2, LogIn, UserCircle, Crown } from 'lucide-react';
import { SuperAdminService, BusinessUser } from '../../services/superAdminService';

interface BusinessUserListProps {
  businessId: string;
  businessName: string;
  onRequestRemoteAccess: (user: BusinessUser) => void;
  /** Direct login (impersonate) as this user — same flow as the business Login button. */
  onLoginAs?: (user: BusinessUser) => void;
  /** auth_user_id currently being impersonated (shows a spinner on that row). */
  loginAsUserId?: string | null;
}

export function BusinessUserList({
  businessId,
  businessName,
  onRequestRemoteAccess,
  onLoginAs,
  loginAsUserId,
}: BusinessUserListProps) {
  const [users, setUsers] = useState<BusinessUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, [businessId]);

  const loadUsers = async () => {
    setLoading(true);
    const data = await SuperAdminService.getBusinessUsers(businessId);
    setUsers(data);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
        <span className="ml-2 text-sm text-gray-600">Loading users...</span>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8">
        <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">No active users found for this business</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center space-x-2 mb-3">
        <Users className="h-4 w-4 text-purple-600" />
        <h4 className="text-sm font-semibold text-gray-900">
          Users &amp; Accounts ({users.length})
        </h4>
      </div>
      <div className="space-y-2">
        {users.map((user) => {
          const isOwner = user.role === 'owner';
          return (
            <div
              key={user.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                isOwner
                  ? 'bg-purple-50 border-purple-200'
                  : 'bg-gray-50 border-gray-100'
              }`}
            >
              <div className="flex items-center space-x-3">
                {isOwner ? (
                  <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <Crown className="h-4 w-4 text-purple-600" />
                  </div>
                ) : (
                  <UserCircle className="h-8 w-8 text-gray-400" />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                  <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded mt-0.5 ${
                    isOwner
                      ? 'bg-purple-100 text-purple-700'
                      : user.role === 'admin'
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-gray-100 text-gray-600'
                  }`}>
                    {isOwner ? 'Account Owner' : user.role}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {onLoginAs && user.auth_user_id ? (
                  <button
                    onClick={() => onLoginAs(user)}
                    disabled={!!loginAsUserId}
                    className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium disabled:opacity-50"
                    title="Log into this account"
                  >
                    {loginAsUserId === user.auth_user_id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <LogIn className="h-3.5 w-3.5" />
                    )}
                    <span>Login</span>
                  </button>
                ) : null}
                <button
                  onClick={() => onRequestRemoteAccess(user)}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                  title="Request remote screen access"
                >
                  <Monitor className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Remote Access</span>
                  <span className="sm:hidden">Remote</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
